import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext } from 'react';
import { T, t, FormattedDateTime } from '@sm/intl';
import { createUseStyles } from 'react-jss';
import { IconFaceHappy, IconFaceNeutral, IconFaceSad, IconHelpFilled } from '@wds/icons';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { sanitizeString } from '@sm/utils';
import ThemeMenu from '../ThemeMenu';
import ThemeBadge from './ThemeBadge';
import { OECSentiment, OECTagType } from '../../../types';
import { buildThemesForResponse } from '../../../lib';
import OpenEndedCodingContext from '../../../OpenEndedCodingContext';
import { useGeneral, useThemes, useResponses } from '../../../Context';
export const COPY = {
  button: {
    id: 'OpenEndedCoding.ThemeSelector.button',
    defaultMessage: 'Add theme',
    description: '[Type: Header][Vis: med] - button text theme selection'
  },
  iconHelpFilled: {
    id: 'OpenEndedCoding.ThemeSelector.iconHelpFilled',
    defaultMessage: 'Help filled icon',
    description: '[Type: Label][Vis: low] - help filled icon'
  },
  iconNegative: {
    id: 'OpenEndedCoding.ThemeSelector.iconNegative',
    defaultMessage: 'Negative icon',
    description: '[Type: Label][Vis: low] - negative icon'
  },
  iconNeutral: {
    id: 'OpenEndedCoding.ThemeSelector.iconNeutral',
    defaultMessage: 'Neutral icon',
    description: '[Type: Label][Vis: low] -r neutral icon'
  },
  iconPositive: {
    id: 'OpenEndedCoding.ThemeSelector.iconPositive',
    defaultMessage: 'Positive icon',
    description: '[Type: Label][Vis: low] - positive icon'
  },
  responseText: {
    id: 'OpenEndedCoding.ThemeSelector.responseText',
    defaultMessage: 'Response ID {respondentId}',
    description: '[Type: Header][Vis: med] - response text ID CTM'
  }
};
const useStyles = createUseStyles(theme => {
  return {
    bullet: {
      color: theme.palette.text.darkMuted
    },
    badges: {
      display: 'inline-flex',
      flexWrap: 'wrap',
      maxWidth: '500px',
      alignItems: 'center'
    }
  };
});
const BulletItem = ({
  children,
  showBullet = false
}) => {
  const classes = useStyles();
  return _jsxs(Box, {
    display: "inline",
    children: [_jsx(Typography, {
      component: "span",
      variant: "bodySm",
      color: "darkMuted",
      children: children
    }), showBullet && _jsx(Box, {
      px: 1,
      display: "inline",
      className: classes.bullet,
      children: "\u2022"
    })]
  });
};
export const getSentimentIcon = sentiment => {
  const sentimentIcon = {
    [OECSentiment.SM_POSITIVE]: _jsx(IconFaceHappy, {
      title: t(COPY.iconPositive),
      color: "success",
      size: "md"
    }),
    [OECSentiment.SM_NEGATIVE]: _jsx(IconFaceSad, {
      title: t(COPY.iconNegative),
      color: "error",
      size: "md"
    }),
    [OECSentiment.SM_NEUTRAL]: _jsx(IconFaceNeutral, {
      title: t(COPY.iconNeutral),
      size: "md",
      color: "upgrade"
    }),
    [OECSentiment.SM_UNDETECTED]: _jsx(IconHelpFilled, {
      title: t(COPY.iconHelpFilled),
      size: "md",
      color: "error"
    })
  };
  return sentimentIcon[sentiment];
};
const ResponseCard = ({
  response
}) => {
  const classes = useStyles();
  const {
    handleCreateThemeForResponse,
    handleAddThemesToResponse,
    handleRemoveThemesFromResponse
  } = useContext(OpenEndedCodingContext);
  const {
    isSolutions
  } = useGeneral();
  const {
    themes
  } = useThemes();
  const {
    showSA
  } = useResponses();
  const themesForResponse = buildThemesForResponse({
    response,
    themes
  });
  const hasThemes = themesForResponse.length > 0;
  const themesAvailableForSelection = themes.filter(theme => themesForResponse.filter(responseTheme => {
    var _a;
    return theme.id === responseTheme.id || ((_a = theme === null || theme === void 0 ? void 0 : theme.autogeneratedTag) === null || _a === void 0 ? void 0 : _a.id) === responseTheme.id;
  }).length === 0);
  const theDate = new Date(response.date);
  return _jsxs(Box, {
    py: 4,
    children: [_jsxs(Box, {
      display: "flex",
      alignItems: "center",
      mb: 3,
      children: [showSA && response.sentiment !== OECSentiment.SM_NOT_ENABLED && _jsx(Box, {
        mr: 1,
        children: getSentimentIcon(response.sentiment)
      }), _jsx(Typography, {
        component: "p",
        variant: "body",
        children: response.content
      })]
    }), _jsxs(Box, {
      className: classes.badges,
      mb: 2,
      children: [hasThemes && themesForResponse.map(({
        id,
        theme,
        tagType
      }) => _jsx(Box, {
        mr: 2,
        children: _jsx(ThemeBadge, {
          isGenerated: tagType === OECTagType.OECMODEL,
          theme: theme,
          onClose: () => handleRemoveThemesFromResponse(id, response.responseTextId)
        })
      }, id)), _jsx(ThemeMenu, {
        responseTextId: response.responseTextId,
        allThemes: themes,
        themesAvailableForSelection: themesAvailableForSelection,
        addThemesToResponse: themeIds => handleAddThemesToResponse(themeIds, response.responseTextId),
        createThemeForResponse: name => handleCreateThemeForResponse(name, response.responseTextId)
      })]
    }), _jsxs(Box, {
      children: [isSolutions && _jsx(BulletItem, {
        showBullet: true,
        children: _jsx("span", {
          dangerouslySetInnerHTML: {
            __html: sanitizeString(response.stimulus)
          }
        })
      }), _jsx(BulletItem, {
        showBullet: true,
        children: _jsx(T, {
          desc: COPY.responseText,
          values: {
            respondentId: response.respondentId
          }
        })
      }), _jsx(BulletItem, {
        children: _jsx("time", {
          dateTime: theDate.toISOString(),
          children: _jsx(FormattedDateTime, {
            value: theDate,
            year: "numeric",
            month: "long",
            day: "2-digit",
            hour: "numeric",
            minute: "numeric",
            timeZone: "utc"
          })
        })
      })]
    })]
  });
};
export default ResponseCard;